import { getAppMode, type AppMode } from './appMode';
import { getTelegramUser } from './telegram';

type AnalyticsEventName = 'site_visit' | 'add_to_cart';
type AnalyticsSource = AppMode;

interface AnalyticsEventPayload {
  event_name: AnalyticsEventName;
  payload?: Record<string, unknown>;
}

const anonymousIdKey = 'pchelovod-anonymous-id';
const siteVisitSentKey = 'pchelovod-site-visit-sent';

function apiBaseUrl() {
  return (import.meta.env.VITE_API_BASE_URL || '').replace(/\/$/, '');
}

function source(): AnalyticsSource {
  return getAppMode();
}

function analyticsIdentity() {
  const telegramUser = getTelegramUser();
  if (telegramUser) {
    return {
      anonymous_id: undefined,
      telegram_id: String(telegramUser.id),
    };
  }

  return {
    anonymous_id: getAnonymousId(),
    telegram_id: undefined,
  };
}

export function getAnonymousId() {
  try {
    const existing = localStorage.getItem(anonymousIdKey);
    if (existing) return existing;

    const next = crypto.randomUUID();
    localStorage.setItem(anonymousIdKey, next);
    return next;
  } catch {
    return crypto.randomUUID();
  }
}

export function trackSiteVisit() {
  const visitSentKey = `${siteVisitSentKey}:${source()}`;

  try {
    if (localStorage.getItem(visitSentKey)) return;
  } catch {
    // Backend-side uniqueness still protects us if browser storage is blocked.
  }

  sendAnalyticsEvent({
    event_name: 'site_visit',
    payload: {
      path: window.location.pathname,
      search: window.location.search,
      referrer: document.referrer || null,
      title: document.title,
      user_agent: navigator.userAgent,
    },
    onSuccess: () => {
      try {
        localStorage.setItem(visitSentKey, '1');
      } catch {
        // Ignore storage errors.
      }
    },
  });
}

export function trackAddToCart(payload: Record<string, unknown>) {
  sendAnalyticsEvent({
    event_name: 'add_to_cart',
    payload: {
      ...payload,
      path: window.location.pathname,
      search: window.location.search,
    },
  });
}

function sendAnalyticsEvent(event: AnalyticsEventPayload & { onSuccess?: () => void }) {
  const baseUrl = apiBaseUrl();
  if (!baseUrl) return;
  const identity = analyticsIdentity();

  void fetch(`${baseUrl}/analytics/events`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({
      event_name: event.event_name,
      source: source(),
      ...identity,
      payload: event.payload ?? {},
    }),
    keepalive: true,
  })
    .then((response) => {
      if (response.ok) {
        event.onSuccess?.();
      }
    })
    .catch(() => {
      // Analytics should never block storefront flows.
    });
}
